/*
 * Copyright Higharc Inc. 2019
 */
import {
  ClampToEdgeWrapping,
  Color,
  DataTexture,
  FloatType,
  LinearFilter,
  Matrix4,
  NearestFilter,
  RGBAFormat,
  UnsignedByteType,
  UVMapping,
  Vector3,
} from "three";
import { BitmapUtil } from "./BitmapUtil";
import { CubeCameraUtil } from "./CubeCameraUtil";
import { SH } from "./SH";
import type { SHLightProbe } from "./SHLightProbe";

/**
 * Subset of {@link SHLightProbeGrid} required to pack probe GPU data.
 * Lets this module avoid importing the concrete grid class (breaks a circular dependency).
 */
export interface SHLightProbeGridForData {
  worldToGridMatrix: Matrix4;
  probeCountX: number;
  probeCountY: number;
  probeCountZ: number;
  shOrder: number | undefined;
  packedCubeMapResolution: number;
  lightProbes: SHLightProbe[];
  lightProbeIndex(gridX: number, gridY: number, gridZ: number): number;
}

// RGBM magnitude encoding factor. This determines the supported dynamic range.
const ENCODE_MAGNITUDE_TO_UNSIGNED_BYTE_MULT = 0.1 * 255;

function getInterleavedCubeDataTextureWidth(probeCountX, _probeCountY, probeCountZ, _cubeResolution) {
  return probeCountX * probeCountZ * 6;
}

function getInterleavedCubeDataTextureHeight(_probeCountX, probeCountY, _probeCountZ, cubeResolution) {
  return cubeResolution * cubeResolution * probeCountY;
}

function initInterleavedCubeArray(textureWidth, textureHeight, fillRGB = undefined) {
  const array = new Uint8Array(textureWidth * textureHeight * 4);
  if (fillRGB !== undefined) {
    const fillPixelUint = [
      Math.round(fillRGB[0] * 255),
      Math.round(fillRGB[1] * 255),
      Math.round(fillRGB[2] * 255),
      Math.round(ENCODE_MAGNITUDE_TO_UNSIGNED_BYTE_MULT), // magnitude 1.0
    ];
    for (let i = 0, l = array.length; i < l; i += 4) {
      array[i] = fillPixelUint[0];
      array[i + 1] = fillPixelUint[1];
      array[i + 2] = fillPixelUint[2];
      array[i + 3] = fillPixelUint[3];
    }
  }
  return array;
}

function initInterleavedCubeTexture(textureWidth, textureHeight, array) {
  const texture = new DataTexture(
    array,
    textureWidth,
    textureHeight,
    RGBAFormat,
    UnsignedByteType,
    UVMapping,
    ClampToEdgeWrapping,
    ClampToEdgeWrapping,
    LinearFilter,
    LinearFilter,
  );
  texture.needsUpdate = true;
  return texture;
}

export function fillInterleavedCubeTexture(probeCountX, probeCountY, probeCountZ, cubeResolution, fillRGB) {
  const textureWidth = getInterleavedCubeDataTextureWidth(probeCountX, probeCountY, probeCountZ, cubeResolution);
  const textureHeight = getInterleavedCubeDataTextureHeight(probeCountX, probeCountY, probeCountZ, cubeResolution);
  const array = initInterleavedCubeArray(textureWidth, textureHeight, fillRGB);
  return initInterleavedCubeTexture(textureWidth, textureHeight, array);
}

export class SHLightProbeGridData {
  refCount: number = 1;
  bounceCount: number = 0;
  private grid?: SHLightProbeGridForData;
  shOrder?: number;
  shLightProbeData?: Float32Array;
  interleavedSHLightProbeData?: Float32Array;

  interleavedCubeLightProbeData;

  shDataTexture?: DataTexture;

  interleavedSHDataTexture?: DataTexture;
  private interleavedSHTextureWidth: number = 0;
  private interleavedSHTextureHeight: number = 0;

  interleavedCubeDataTexture?: DataTexture;
  private interleavedCubeTextureWidth: number = 0;
  private interleavedCubeTextureHeight: number = 0;

  cubeMapResolution: number = 1;

  probeCountX: number = 0;
  probeCountY: number = 0;
  probeCountZ: number = 0;

  worldToGridMatrix;

  constructor(grid?: SHLightProbeGridForData) {
    this.worldToGridMatrix = new Matrix4();
    this.grid = grid;
  }

  ownedBy(grid: SHLightProbeGridForData) {
    return this.grid === grid;
  }

  initFromProbes(grid: SHLightProbeGridForData) {
    this.worldToGridMatrix = grid.worldToGridMatrix;
    this.reinitStorage(
      grid.probeCountX,
      grid.probeCountY,
      grid.probeCountZ,
      grid.shOrder,
      grid.packedCubeMapResolution,
    );
    this.grid = grid;
  }

  // Fills the interleaved cube maps with a smooth gradient from top to bottom, assuming the up direction is (0, 0, 1).
  initAsSimpleGradient(cubeMapResolution, topColor, horizonColor, bottomColor) {
    this.worldToGridMatrix = new Matrix4();
    this.bounceCount = 1;
    this.reinitStorage(1, 1, 1, undefined, cubeMapResolution);

    const array = this.interleavedCubeLightProbeData;

    const direction = new Vector3();
    for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
      for (let cubeX = 0; cubeX < cubeMapResolution; ++cubeX) {
        for (let cubeY = 0; cubeY < cubeMapResolution; ++cubeY) {
          const texelCenterX = ((cubeX + 0.5) / cubeMapResolution) * 2.0 - 1.0;
          const texelCenterY = ((cubeY + 0.5) / cubeMapResolution) * 2.0 - 1.0;
          CubeCameraUtil.getCubeMapTexelDirection(faceIndex, texelCenterX, texelCenterY, direction);
          const color = new Color(horizonColor);
          if (direction.z > 0) {
            const topMult = Math.max(direction.z, 0);
            color.lerp(topColor, topMult);
          } else {
            const bottomMult = Math.max(-direction.z, 0);
            color.lerp(bottomColor, bottomMult);
          }
          const dataIndex = this.lightProbeInterleavedCubeDataIndex(0, 0, 0, faceIndex, cubeX, cubeY);
          array[dataIndex] = Math.round(color.r * 255);
          array[dataIndex + 1] = Math.round(color.g * 255);
          array[dataIndex + 2] = Math.round(color.b * 255);
          array[dataIndex + 3] = ENCODE_MAGNITUDE_TO_UNSIGNED_BYTE_MULT; // magnitude 1.0
        }
      }
    }
    if (this.interleavedCubeDataTexture) {
      this.interleavedCubeDataTexture.needsUpdate = true;
    }
  }

  reinitStorage(probeCountX, probeCountY, probeCountZ, shOrder, cubeMapResolution) {
    this.dispose();
    this.shDataTexture = undefined;
    this.interleavedSHDataTexture = undefined;
    this.interleavedCubeDataTexture = undefined;

    this.probeCountX = probeCountX;
    this.probeCountY = probeCountY;
    this.probeCountZ = probeCountZ;

    if (shOrder !== undefined) {
      this.shOrder = shOrder;
      const coefficientVectorCount = SH.getCoefficientVectorCount(this.shOrder);
      const shDataTextureHeight = probeCountX * probeCountY * probeCountZ;
      const shDataTextureWidth = coefficientVectorCount;

      // We have a data interleaving scheme for light probes stored as SH so that we can use linear filtered texture
      // sampling to blend between probes that are neighboring in the x/y dimensions.
      // Here, P123c4 means Probe with grid coordinates x = 1, y = 2, z = 3, coefficient 4.
      // The order of the coefficients in the data texture is:

      // P000c0 P100c0   P001c0 P101c0   P002c0 P102c0
      // P010c0 P110c0   P011c0 P111c0   P012c0 P112c0

      // P000c1 P100c1   P001c1 P101c1   P002c1 P102c1
      // P010c1 P110c1   P011c1 P111c1   P012c1 P112c1

      // P000c2 P100c2   P001c2 P101c2   P002c2 P102c2
      // P010c2 P110c2   P011c2 P111c2   P012c2 P112c2

      // So the coefficient index affects the higher-order bits of the y coordinate in the texture.
      // and the z coordinate affects the higher-order bits of the x coordinate in the texture.

      this.interleavedSHTextureWidth = this.probeCountX * this.probeCountZ;
      this.interleavedSHTextureHeight = this.probeCountY * coefficientVectorCount;

      this.shLightProbeData = new Float32Array(shDataTextureWidth * shDataTextureHeight * 4);
      this.interleavedSHLightProbeData = new Float32Array(
        this.interleavedSHTextureWidth * this.interleavedSHTextureHeight * 4,
      );

      // In dataTexture each row of the texture corresponds to one lightprobe.
      // TODO: Possible to pack coefficients into a Uint8Array as an alternative? Could use lower precision or pack each
      // float into multiple color channels.
      this.shDataTexture = new DataTexture(
        this.shLightProbeData,
        shDataTextureWidth,
        shDataTextureHeight,
        RGBAFormat,
        FloatType,
        UVMapping,
        ClampToEdgeWrapping,
        ClampToEdgeWrapping,
        NearestFilter,
        NearestFilter,
      );
      this.shDataTexture.needsUpdate = true;

      // In interleavedSHDataTexture the probes stored as SH are interleaved according to the interleaving scheme detailed above.
      this.interleavedSHDataTexture = new DataTexture(
        this.interleavedSHLightProbeData,
        this.interleavedSHTextureWidth,
        this.interleavedSHTextureHeight,
        RGBAFormat,
        FloatType,
        UVMapping,
        ClampToEdgeWrapping,
        ClampToEdgeWrapping,
        LinearFilter,
        LinearFilter,
      );
      this.interleavedSHDataTexture.needsUpdate = true;
    }

    if (cubeMapResolution !== undefined) {
      this.cubeMapResolution = cubeMapResolution;
      // We have a similar interleaving scheme for light probes stored as low-resolution cube maps.
      // With cube maps, the x axis coordinate in the atlas is:
      // (cubeMapFaceIndex * probeCountZ + probe.z) * probeCountX + probe.x
      // And y coordinate in the atlas is:
      // (cubeMapFace.y * cubeMapWidth + cubeMapFace.x) * probeCountY + probe.y
      // So all the pixels on a single cube map face are stored in the same column in the atlas.

      // We encode the pixels as RGBM in an unsigned byte buffer,
      // so that we can linearly interpolate an atlas of high dynamic range numbers regardless of WebGL support level.

      this.interleavedCubeTextureWidth = getInterleavedCubeDataTextureWidth(
        this.probeCountX,
        this.probeCountY,
        this.probeCountZ,
        this.cubeMapResolution,
      );
      this.interleavedCubeTextureHeight = getInterleavedCubeDataTextureHeight(
        this.probeCountX,
        this.probeCountY,
        this.probeCountZ,
        this.cubeMapResolution,
      );

      this.interleavedCubeLightProbeData = initInterleavedCubeArray(
        this.interleavedCubeTextureWidth,
        this.interleavedCubeTextureHeight,
      );

      // In interleavedCubeDataTexture we store interleaved cube maps.
      this.interleavedCubeDataTexture = initInterleavedCubeTexture(
        this.interleavedCubeTextureWidth,
        this.interleavedCubeTextureHeight,
        this.interleavedCubeLightProbeData,
      );
    }
  }

  lightProbeSHDataIndex(lightProbeIndex, coefficientIndex) {
    const textureCoordinateIndex = lightProbeIndex * SH.getCoefficientVectorCount(this.shOrder) + coefficientIndex;
    return textureCoordinateIndex * 4;
  }

  lightProbeInterleavedSHDataIndex(gridX, gridY, gridZ, coefficientIndex) {
    const textureRow = coefficientIndex * this.probeCountY + gridY;
    const textureCoordinateIndex = textureRow * this.interleavedSHTextureWidth + gridZ * this.probeCountX + gridX;
    return textureCoordinateIndex * 4;
  }

  lightProbeInterleavedCubeDataIndex(gridX, gridY, gridZ, cubeFace, cubeX, cubeY) {
    const textureRow =
      ((this.cubeMapResolution - cubeY - 1) * this.cubeMapResolution + cubeX) * this.probeCountY + gridY;
    const textureCoordinateIndex =
      textureRow * this.interleavedCubeTextureWidth + (cubeFace * this.probeCountZ + gridZ) * this.probeCountX + gridX;
    return textureCoordinateIndex * 4;
  }

  updateFromProbes(bounceCount) {
    if (!this.grid) {
      return;
    }
    this.bounceCount = bounceCount;

    console.assert(this.probeCountX * this.probeCountY * this.probeCountZ === this.grid.lightProbes.length);
    console.assert(this.shOrder === this.grid.shOrder);
    console.assert(this.cubeMapResolution === this.grid.packedCubeMapResolution);

    if (this.shOrder !== undefined && this.shLightProbeData && this.interleavedSHLightProbeData) {
      const coefficientVectorCount = SH.getCoefficientVectorCount(this.shOrder);
      for (let probeIndex = 0, len = this.grid.lightProbes.length; probeIndex < len; ++probeIndex) {
        const probe = this.grid.lightProbes[probeIndex];
        if (!probe.colorSHCoefficients) {
          continue;
        }
        for (let coefficientIndex = 0; coefficientIndex < coefficientVectorCount; ++coefficientIndex) {
          const dataIndex = this.lightProbeSHDataIndex(probeIndex, coefficientIndex);
          this.shLightProbeData[dataIndex] = probe.colorSHCoefficients[coefficientIndex].x;
          this.shLightProbeData[dataIndex + 1] = probe.colorSHCoefficients[coefficientIndex].y;
          this.shLightProbeData[dataIndex + 2] = probe.colorSHCoefficients[coefficientIndex].z;
        }
      }

      const probeCountX = this.probeCountX;
      const probeCountY = this.probeCountY;
      const probeCountZ = this.probeCountZ;

      for (let coefficientIndex = 0; coefficientIndex < coefficientVectorCount; ++coefficientIndex) {
        for (let gridY = 0; gridY < probeCountY; ++gridY) {
          for (let gridZ = 0; gridZ < probeCountZ; ++gridZ) {
            for (let gridX = 0; gridX < probeCountX; ++gridX) {
              const dataIndex = this.lightProbeInterleavedSHDataIndex(gridX, gridY, gridZ, coefficientIndex);
              const probe = this.grid.lightProbes[this.grid.lightProbeIndex(gridX, gridY, gridZ)];
              if (!probe.colorSHCoefficients) {
                continue;
              }
              this.interleavedSHLightProbeData[dataIndex] = probe.colorSHCoefficients[coefficientIndex].x;
              this.interleavedSHLightProbeData[dataIndex + 1] = probe.colorSHCoefficients[coefficientIndex].y;
              this.interleavedSHLightProbeData[dataIndex + 2] = probe.colorSHCoefficients[coefficientIndex].z;
            }
          }
        }
      }
    }

    const packedCubeMapResolution = this.cubeMapResolution;
    if (
      packedCubeMapResolution !== undefined &&
      this.grid.lightProbes[0].storedIrradianceCubeResolution >= packedCubeMapResolution
    ) {
      // const startTime = performance.now();
      const probeCountX = this.probeCountX;
      const probeCountY = this.probeCountY;
      const probeCountZ = this.probeCountZ;

      for (let gridY = 0; gridY < probeCountY; ++gridY) {
        for (let gridZ = 0; gridZ < probeCountZ; ++gridZ) {
          for (let gridX = 0; gridX < probeCountX; ++gridX) {
            const probe = this.grid.lightProbes[this.grid.lightProbeIndex(gridX, gridY, gridZ)];
            for (let cubeFaceIndex = 0; cubeFaceIndex < 6; ++cubeFaceIndex) {
              let cubeFace = probe.irradianceFaces[cubeFaceIndex];
              let cubeFaceResolution = probe.storedIrradianceCubeResolution;
              while (cubeFaceResolution > packedCubeMapResolution) {
                const targetResolution = Math.max(packedCubeMapResolution, cubeFaceResolution / 2);
                cubeFace = BitmapUtil.linearDownsampleBitmap(
                  cubeFace,
                  cubeFaceResolution,
                  cubeFaceResolution,
                  targetResolution,
                  targetResolution,
                  4,
                );
                cubeFaceResolution = targetResolution;
              }
              for (let cubeY = 0; cubeY < packedCubeMapResolution; ++cubeY) {
                for (let cubeX = 0; cubeX < packedCubeMapResolution; ++cubeX) {
                  const dataIndex = this.lightProbeInterleavedCubeDataIndex(
                    gridX,
                    gridY,
                    gridZ,
                    cubeFaceIndex,
                    cubeX,
                    cubeY,
                  );
                  const cubePixelDataIndex = (cubeY * cubeFaceResolution + cubeX) * 4;
                  const r = cubeFace[cubePixelDataIndex];
                  const g = cubeFace[cubePixelDataIndex + 1];
                  const b = cubeFace[cubePixelDataIndex + 2];
                  const m = Math.max(r, g, b);
                  const ditherBias = Math.random() - 0.5;
                  this.interleavedCubeLightProbeData[dataIndex] = Math.round((r / m) * 255);
                  this.interleavedCubeLightProbeData[dataIndex + 1] = Math.round((g / m) * 255);
                  this.interleavedCubeLightProbeData[dataIndex + 2] = Math.round((b / m) * 255);
                  this.interleavedCubeLightProbeData[dataIndex + 3] = Math.max(
                    0,
                    Math.min(255, Math.round(m * ENCODE_MAGNITUDE_TO_UNSIGNED_BYTE_MULT + ditherBias)),
                  );
                }
              }
            }
          }
        }
      }
      // console.log(`Interleaved cube map data: ${performance.now() - startTime} ms`);
    }

    if (this.shDataTexture !== undefined) {
      this.shDataTexture.needsUpdate = true;
    }
    if (this.interleavedSHDataTexture !== undefined) {
      this.interleavedSHDataTexture.needsUpdate = true;
    }
    if (this.interleavedCubeDataTexture !== undefined) {
      this.interleavedCubeDataTexture.needsUpdate = true;
    }
  }

  dispose() {
    if (this.shDataTexture !== undefined) {
      this.shDataTexture.dispose();
    }
    if (this.interleavedSHDataTexture !== undefined) {
      this.interleavedSHDataTexture.dispose();
    }
    if (this.interleavedCubeDataTexture !== undefined) {
      this.interleavedCubeDataTexture.dispose();
    }
    this.bounceCount = 0;
  }
}
